import { clean, decodeEntities, stripHtml } from './quinn';
import { createStore } from './store';

export interface Faq {
  question?: string;
  answer?: string;
  category?: string;
}

export interface FaqItem {
  key: string;
  question: string;
  /** Sanitized answer HTML: paragraphs, lists, emphasis and links only. */
  html: string;
  category: string;
  text: string;
}

/**
 * The seven topics and their order come from Quinn's live FAQ page, which groups the same titles every
 * community carries. MultiHub sends no category today, so a question's own category wins, then this title map.
 * Order matters: "Pet Fees" files under the fee guide, as on the live page.
 */
const RULES: Array<[string, RegExp]> = [
  ['Deposit & Mandatory Fee Guide', /application fee|administration fee|admin fee|additional charge|utility|pet fee|concierge|smart home|solar|trash|landscap|package fee/i],
  ['Application Requirements', /^application$|application requirement|occupan|guarantor|co-?signer/i],
  ['Credit & Finance', /income|security deposit|credit|employment/i],
  ['Leasing History', /rental history|criminal|eviction|background/i],
  ['Pets & Animals', /\bpets?\b|animal/i],
  ['Vehicles', /vehicle|parking|garage/i],
  ['Renters Insurance', /insurance/i],
];
export const GENERAL = 'More questions';

export function faqCategory(f: Faq): string {
  const own = clean(f.category);
  if (own) return own;
  const q = stripHtml(f.question);
  return RULES.find(([, rx]) => rx.test(q))?.[0] || GENERAL;
}

/** One contextual CTA under an open answer, chosen by its topic. */
export type CtaKind = 'apply' | 'fees' | 'tour' | 'pets' | 'contact';
export const CTA_FOR: Record<string, CtaKind> = {
  'Application Requirements': 'apply',
  'Credit & Finance': 'apply',
  'Leasing History': 'apply',
  'Deposit & Mandatory Fee Guide': 'fees',
  'Renters Insurance': 'tour',
  'Pets & Animals': 'pets',
};
export const ctaFor = (category: string): CtaKind => CTA_FOR[category] || 'contact';

const ALLOWED = new Set(['p', 'br', 'a', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li']);

/** Keeps paragraphs, lists, emphasis and safe links; every other tag is dropped and its text kept. */
export function sanitizeAnswer(raw: string | null | undefined): string {
  let h = String(raw ?? '').replace(/<(script|style|iframe|object)[\s\S]*?<\/\1>/gi, '');
  if (!/<(p|ul|ol|br)[\s>/]/i.test(h)) {
    h = h
      .split(/\n\s*\n/)
      .map((para) => para.trim())
      .filter(Boolean)
      .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
      .join('');
  }
  return h.replace(/<\/?([a-z0-9]+)([^>]*)>/gi, (tag, name: string, attrs: string) => {
    const n = name.toLowerCase();
    if (!ALLOWED.has(n)) return '';
    if (tag.startsWith('</')) return `</${n}>`;
    if (n !== 'a') return `<${n}>`;
    const href = /href\s*=\s*["']([^"']+)["']/i.exec(attrs)?.[1] || '';
    if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href.trim())) return '<a>';
    const external = /^https?:/i.test(href);
    return `<a href="${href.replace(/"/g, '&quot;')}"${external ? ' target="_blank" rel="noopener"' : ''}>`;
  });
}

export function toFaqItems(faqs: Faq[]): FaqItem[] {
  return faqs
    .filter((f) => clean(f.question) && clean(f.answer))
    .map((f, i) => {
      const question = stripHtml(f.question);
      const html = sanitizeAnswer(f.answer);
      return { key: `${i}-${question}`, question, html, category: faqCategory(f), text: `${question} ${stripHtml(html)}`.toLowerCase() };
    });
}

/** Topics in the live page's order, then any of the property's own, then More questions; empty topics hide. */
export function faqCategories(items: FaqItem[]): Array<{ name: string; count: number }> {
  const counts = new Map<string, number>();
  items.forEach((it) => counts.set(it.category, (counts.get(it.category) || 0) + 1));
  const order = [...RULES.map(([n]) => n), ...[...counts.keys()].filter((n) => n !== GENERAL && !RULES.some(([r]) => r === n)), GENERAL];
  return order.filter((n) => counts.has(n)).map((name) => ({ name, count: counts.get(name)! }));
}

/** A search for "pet" also finds dogs, cats and assistance animals. */
const SYNONYMS: Record<string, string[]> = {
  pet: ['pets', 'animal', 'dog', 'cat'],
  pets: ['pet', 'animal', 'dog', 'cat'],
  animal: ['pet', 'animals'],
  dog: ['pet', 'dogs'],
  cat: ['pet', 'cats'],
  car: ['vehicle', 'parking'],
  parking: ['vehicle', 'park'],
  fee: ['fees', 'charge', 'cost'],
  cost: ['fee', 'charge', 'price'],
  deposit: ['deposits'],
  cosigner: ['guarantor'],
};

const words = (query: string) => query.toLowerCase().split(/\s+/).filter(Boolean);

/** Every word of the query (or one of its synonyms) appears in the question or answer. */
export function matches(it: FaqItem, query: string): boolean {
  return words(query).every((w) => [w, ...(SYNONYMS[w] || [])].some((t) => it.text.includes(t)));
}

/** The terms to mark in results: the query words and whichever synonyms actually occur. */
export function markTerms(items: FaqItem[], query: string): string[] {
  const out = new Set<string>();
  words(query).forEach((w) => {
    out.add(w);
    (SYNONYMS[w] || []).forEach((s) => items.some((it) => it.text.includes(s)) && out.add(s));
  });
  return [...out].sort((a, b) => b.length - a.length);
}

const escapeRx = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export const termRx = (terms: string[]) => (terms.length ? new RegExp(`(${terms.map(escapeRx).join('|')})`, 'gi') : null);

/** Wraps matches in <mark>, touching text only, never tags or attributes. */
export function markHtml(html: string, terms: string[]): string {
  const rx = termRx(terms);
  if (!rx) return html;
  return html
    .split(/(<[^>]+>|&#?\w+;)/)
    .map((part) => (part.startsWith('<') || /^&#?\w+;$/.test(part) ? part : part.replace(rx, '<mark>$1</mark>')))
    .join('');
}

/** Plain-text answer around the first match, for the suggestion list: "…allowed parking locations. If your…". */
export function snippet(it: FaqItem, terms: string[], width = 72): string {
  const text = decodeEntities(stripHtml(it.html));
  const lower = text.toLowerCase();
  const at = terms.map((t) => lower.indexOf(t)).filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (at == null) return text.slice(0, width) + (text.length > width ? '…' : '');
  const start = Math.max(0, at - Math.round(width / 3));
  const end = Math.min(text.length, start + width);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}

/** Popular topics, shown only when the property has the question: [link text, question pattern]. */
export const POPULAR: Array<[string, RegExp]> = [
  ['Pets', /^pets$/i],
  ['Application fee', /^application fee/i],
  ['Income', /^income/i],
  ['Security deposit', /^security deposit/i],
  ['Vehicles', /^vehicles?$/i],
];

export interface FaqState {
  query: string;
  category: string;
  /** Question to open and scroll to (a Popular link or a suggestion). */
  focus: string | null;
}

export const faqStore = createStore<FaqState>({ query: '', category: 'all', focus: null });
